import MojitoPresentation from 'mojito/presentation';
import { useEffect, useMemo, useRef, useState } from 'react';

const { FlexPane, Button, Text, AbsolutePane } = MojitoPresentation.Components;

const BannerCardDetails = props => {
    const {
        cardBannerDetails,
        onClick,
        mojitoTools: { config },
    } = props;

    const { headlineText, primaryText, secondaryText, primaryButton, secondaryButton } =
        cardBannerDetails;

    return (
        <AbsolutePane config={config.container} class="ta-CardBannerDetails">
            <Headline text={headlineText} config={config} />
            <FlexPane config={config.innerContainer}>
                <PrimaryText config={config} text={primaryText} />
                {(getCardButtonLink(secondaryButton) || getCardButtonLink(primaryButton)) && (
                    <FlexPane config={config.buttonsContainer}>
                        <CardButton
                            buttonInfo={secondaryButton}
                            onClick={onClick}
                            class={'ta-secondary-cardButton'}
                            buttonConfig={config.secondaryButton}
                            labelConfig={config.secondaryButtonLabel}
                        />
                        <CardButton
                            buttonInfo={primaryButton}
                            onClick={onClick}
                            class={'ta-primary-cardButton'}
                            buttonConfig={config.primaryButton}
                            labelConfig={config.primaryButtonLabel}
                        />
                    </FlexPane>
                )}
            </FlexPane>
            <SecondaryText text={secondaryText} config={config} />
        </AbsolutePane>
    );
};

const Headline = ({ text, config }) => {
    if (!text) {
        return null;
    }

    return <Text config={config.headlineText}>{sanitizeText(text)}</Text>;
};

const PrimaryText = props => {
    const { text, config } = props;

    const [primaryTextMaxLines, setPrimaryTextMaxLines] = useState(0);
    const primaryTextContainerRef = useRef();
    const primaryTextRef = useRef();

    useEffect(() => {
        if (primaryTextContainerRef.current && primaryTextRef.current) {
            setPrimaryTextMaxLines(
                calculateAvailableTextLines(primaryTextContainerRef.current, primaryTextRef.current)
            );
        }
    }, []);

    const textConfig = useMemo(
        () => ({
            ...config.primaryText,
            ellipsisOnLineNumber: primaryTextMaxLines,
        }),
        [config.primaryText, primaryTextMaxLines]
    );

    if (!text) {
        return null;
    }

    return (
        <FlexPane config={config.primaryTextContainer} elementRef={primaryTextContainerRef}>
            <Text config={textConfig} textRef={primaryTextRef} class="ta-primaryText">
                {sanitizeText(text)}
            </Text>
        </FlexPane>
    );
};

const SecondaryText = ({ text, config }) => {
    return (
        <FlexPane config={config.secondaryTextWrapper}>
            {text && (
                <Text config={config.secondaryText} class="ta-secondaryText">
                    {sanitizeText(text)}
                </Text>
            )}
        </FlexPane>
    );
};

const CardButton = props => {
    const { buttonInfo, onClick, buttonConfig, labelConfig } = props;

    const hrefLink = getCardButtonLink(buttonInfo);
    if (!hrefLink) {
        return null;
    }

    return (
        <Button
            config={buttonConfig}
            hrefLink={hrefLink}
            onClick={() => onClick(getCardButtonLink(buttonInfo))}
            class={props.class}
        >
            <Text config={labelConfig}>{getCardButtonLabel(buttonInfo)}</Text>
        </Button>
    );
};

const getCardButtonLabel = cardButton => cardButton?.text;
const getCardButtonLink = cardButton => cardButton?.link;
// Sanitize text: replace all whitespace sequences with a single space and trim.
// Not properly formatted text can cause weird IOS Safari rendering loop behavior.
const sanitizeText = input => input.replace(/\s+/g, ' ').trim();

/**
 * Calculates the number of text lines that can fit inside a container element
 * based on the container's height, text element's font size, and line height.
 *
 * This function is useful when you need to determine how many lines of text
 * can be displayed within a certain height, such as for cases where
 * text truncation, ellipsis, or visibility constraints are necessary. It accounts
 * for various CSS line-height formats (e.g., "px", "%", "normal").
 *
 * @private
 * @param {HTMLElement} containerElement - The DOM element representing the container.
 * @param {HTMLElement} textElement - The DOM element representing the text.
 * @returns {number} The maximum number of text lines that can fit within the container.
 */
export function calculateAvailableTextLines(containerElement, textElement) {
    const computedStyleContainer = getComputedStyle(containerElement);
    const computedStyleText = getComputedStyle(textElement);
    const containerHeight = parseFloat(computedStyleContainer.height);
    const fontSize = parseFloat(computedStyleText.fontSize);
    const lineHeight = computedStyleText.lineHeight;

    let lineHeightMultiplier;

    // Calculate the line height multiplier based on the CSS `line-height` format
    if (lineHeight.includes('px')) {
        lineHeightMultiplier = parseFloat(lineHeight) / fontSize;
    } else if (lineHeight.includes('%')) {
        lineHeightMultiplier = parseFloat(lineHeight) / 100;
    } else if (lineHeight.includes('normal')) {
        lineHeightMultiplier = 1.2;
    } else {
        lineHeightMultiplier = parseFloat(lineHeight);
    }

    // Calculate the number of lines that fit into the container height
    return Math.floor(containerHeight / (lineHeightMultiplier * fontSize));
}

export default BannerCardDetails;
